import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { sessionsApi } from "@/lib/api/sessions";
import { epicKeys } from "./use-epics";

export const activeSessionKeys = {
  all: ["active-session"] as const,
  byEpic: (epicId: string) => [...activeSessionKeys.all, epicId] as const,
};

/**
 * Lightweight hook to check if an epic has an active AI session.
 * Polls every 30s by default. Use this to conditionally enable
 * more frequent polling on other queries during active implementation.
 */
export function useActiveSession(epicId: string | undefined) {
  const { data, ...rest } = useQuery({
    queryKey: activeSessionKeys.byEpic(epicId ?? ""),
    queryFn: () => sessionsApi.getActive(epicId!),
    enabled: !!epicId,
    refetchInterval: 30_000,
    select: (res) => res.data,
  });

  return {
    session: data ?? null,
    hasActiveSession: !!data,
    ...rest,
  };
}

/**
 * Mutation to abandon (force-end) a stuck or orphaned active session for an
 * epic. Invalidates the active-session and epic-detail queries so the run
 * command re-appears once the session is cleared.
 */
export function useAbandonSession(epicId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => sessionsApi.abandon(sessionId),
    onSuccess: () => {
      if (!epicId) return;
      void queryClient.invalidateQueries({ queryKey: activeSessionKeys.byEpic(epicId) });
      void queryClient.invalidateQueries({ queryKey: epicKeys.detail(epicId) });
    },
  });
}
